import React from 'react';

import { IconCheck } from '@tabler/icons-react';

export type TapeUploadStep = 1 | 2 | 3;

const STEPS: { step: TapeUploadStep; label: string }[] = [
  { step: 1, label: 'Upload a tape' },
  { step: 2, label: 'Map fields' },
  { step: 3, label: 'Review & save' },
];

type Props = {
  /** Furthest step the selected tape has reached; earlier steps render as done. */
  currentStep: TapeUploadStep;
  /** Steps ≤ maxReachableStep are clickable (their cards are mounted). */
  onStepClick: (step: TapeUploadStep) => void;
};

/**
 * Wayfinding header for the tape upload flow (upload → map → review). The page
 * already reveals a card per step; the stepper names them and tracks which one
 * the selected tape is on, replacing the old passive "NEXT:" hint and the
 * disabled scroll-button. Clicking a reached step scrolls to its card.
 */
const TapeUploadStepper: React.FC<Props> = ({ currentStep, onStepClick }) => (
  <ol data-testid="tape-upload-stepper" className="mb-4 mt-2 flex items-center gap-2">
    {STEPS.map(({ step, label }, idx) => {
      const isDone = step < currentStep;
      const isCurrent = step === currentStep;
      const reachable = step <= currentStep;
      return (
        <React.Fragment key={step}>
          {idx > 0 && (
            <div
              aria-hidden
              className={`h-px w-10 ${isDone || isCurrent ? 'bg-pink-500' : 'bg-slate-600'}`}
            />
          )}
          <li>
            <button
              type="button"
              data-testid={`tape-upload-step-${step}`}
              aria-current={isCurrent ? 'step' : undefined}
              disabled={!reachable}
              onClick={() => onStepClick(step)}
              className={`flex items-center gap-2 rounded-full py-1 pl-1 pr-3 text-sm transition-colors ${
                reachable ? 'cursor-pointer hover:bg-slate-700' : 'cursor-default'
              }`}
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  isCurrent
                    ? 'bg-pink-500 text-white'
                    : isDone
                      ? 'border border-pink-500 text-pink-400'
                      : 'border border-slate-600 text-slate-400'
                }`}
              >
                {isDone ? <IconCheck size={14} stroke={2.5} /> : step}
              </span>
              <span
                className={
                  isCurrent
                    ? 'font-medium text-white'
                    : isDone
                      ? 'text-slate-200'
                      : 'text-slate-400'
                }
              >
                {label}
              </span>
            </button>
          </li>
        </React.Fragment>
      );
    })}
  </ol>
);

export default TapeUploadStepper;
